/* eslint @typescript-eslint/no-unused-vars: 0 */
import { paymentFailedFallbackMessage } from '@helper/constants';
import { EnvFlags, disableEnvs, enableEnvs, mapFeatures } from '@helper/functions/feature-flags';

/**
 * #### Define the feature flags here
 *
 * Outdated or in-production features should have their flags removed to keep it tidy.
 */
export type FeatureFlags<T> = {
  nonProdWarning: T;
  roles: {
    cashier: T;
  };
  paymentMethods: {
    cash: T;
    cheque: T;
    terminal: T;
    payto: T;
    payToNonProd: T;
    bpoint: T;
  };
  services: {
    accommodation: T;
    cremation: T;
    education: T;
    staffPayments: T;
    nswHealthMap: T;
    medicalRecords: T;
    mockCheckout: T;
    pos: T;
    prepaymentEUID: T;
    payToQueryResolution: T;
    paymentPlans: T;
  };
  admin: {
    outageManager: T;
    cashierReport: T;
    reprintReceipt: T;
    ratingReport: T;
    medicalRecords: T;
    organisations: T;
    devTools: T;
    cashierAccess: T;
    QRCodeGeneration: T;
    thirdPartyPayment: T;
    internationalDashboard: T;
    terminalManager: T;
    paymentMethodManager: T;
    paytoAccessManager: T;
  };
  misc: {
    terminal: {
      bankIdentifier: T;
    };
    payto:{
      sd:T;
      enable: T;
      misc: T;
      donation: T;
      pbrc: T;
    };
  };
  userSettings: T;
};

/**
 * #### Toggle the flags here
 *
 * - To partially-enabled a feature:
 *
 * ```
 * awesomeFeature: enableEnvs(['dev', 'test'])    // ✅ Only enable this feature in DEV and TEST instances.
 * testingFeature: disableEnvs(['stg'])           // ✅ Only disable this feature in STG, enable it in ALL other envs.
 *
 * ```
 *
 *
 * - If a feature should be globally enabled or disabled, you can set a boolean value.
 *
 * ```
 * awesomeFeature: true;     // ✅ Globally enabled
 * testingFeature: false;    // ✅ Globally disabled
 * ```
 */
const features: FeatureFlags<EnvFlags | boolean> = {
  nonProdWarning: disableEnvs(['prod']),
  roles: {
    cashier: true,
  },
  paymentMethods: {
    cash: true,
    cheque: true,
    terminal: true,
    payto: true,
    payToNonProd: enableEnvs(['dev', 'test', 'stg', 'uat', 'tstoc', 'uatoc']),
    bpoint: true,
  },
  services: {
    accommodation: enableEnvs(['dev']),
    cremation: false,
    education: false,
    staffPayments: false,
    medicalRecords: false,
    mockCheckout: enableEnvs(['dev']),
    nswHealthMap: enableEnvs(['dev']),
    pos: enableEnvs(['dev']),
    prepaymentEUID: true,
    payToQueryResolution: false,
    paymentPlans: true,
  },
  admin: {
    outageManager: true,
    cashierReport: true,
    ratingReport: false,
    cashierAccess: true,
    reprintReceipt: true,
    QRCodeGeneration: enableEnvs(['dev']),
    medicalRecords: false,
    organisations: false,
    devTools: false,
    thirdPartyPayment: enableEnvs(['dev']),
    internationalDashboard: true,
    terminalManager: true,
    paymentMethodManager: true,
    paytoAccessManager: false,
  },
  misc: {
    terminal: {
      bankIdentifier: true,
    },
    payto: {
      sd: true,
      enable: true,
      misc: true,
      donation: true,
      pbrc: true,
    }
  },
  userSettings: true,
};

/**
 * #### Map and export flags
 *
 * Do not modify below.
 */
const Features = mapFeatures(features as FeatureFlags<EnvFlags | boolean>);
export default Features;
